import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { KioskConfigurationService } from '../kiosk-configuration.service';
import { FormGroup, FormBuilder, Validators, ValidatorFn, FormArray, AbstractControl } from '@angular/forms';
import { KioskConfiguration } from '../model/kiosk-configuration';
import { map } from 'rxjs/operators';
import { LocalizedText } from '../model/localized_text';
import { DirtyFormChecker } from 'src/app/shared/routeguards/DirtyFormChecker';
import { ToastService } from 'src/app/shared/services/toast.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/shared/models/store models/app-state.interface';
import { getACLicenseStatus,getLTLicenseStatus, getUserType } from 'src/app/state/index';
import { UserGridRequestModel } from '../../user/shared/models/userGridRequestModel';
import { PageInfo } from 'src/app/shared/models/queryparams/pageinfo.model';
import { UserType } from '../../user/shared/models/usertype.enum';
import { SortInfo } from 'src/app/shared/models/queryparams/sortinfo.model';
import { UserService } from '../../user/shared/services/user.service';
import { SystemConfigurationService } from '../../system-configuration/shared/services/system-configuration.service';
import { SystemConfigurationValueType } from 'src/app/shared/enums/systemConfiguration.enum';
import { SystemConfigValue } from 'src/app/admin/system-configuration/shared/models/system-config-value';
import { MasterLookupService } from 'src/app/master/master-services/master-lookup.service';
import { NotificationGroup } from 'src/app/master/models/NotificationGroup';
import { WaitTimeOption } from '../model/waittimeoption.enum';
import { TranslationLoaderFacade } from '../../localization/translation-loader.facade';
import { TranslateService } from '@ngx-translate/core';
import { readCastedValue } from 'src/app/shared/utils/read-casted-value.function';

@Component({
  selector: 'app-kiosk-configuration-settings',
  templateUrl: './kiosk-configuration-settings.component.html',
  styleUrls: ['./kiosk-configuration-settings.component.scss']
})
export class KioskConfigurationSettingsComponent implements OnInit, DirtyFormChecker {
  public fontSizes: any;
  public approximateWaitTimeSelect: string = '';

  constructor(private route: ActivatedRoute,
    private kioskConfigService: KioskConfigurationService, public userService: UserService,
    private formBuilder: FormBuilder, private router: Router, private toastService: ToastService,
    private store: Store<AppState>,
    private systemConfigService: SystemConfigurationService,
    private lookupService: MasterLookupService,
    private translationLoaderFacade: TranslationLoaderFacade,
    private translateService: TranslateService) {
      // Define form group.
      this.kioskConfigurationForm = this.formBuilder.group({

      // Tab Display options
      tabsGroup: this.formBuilder.group({
        showSignInTab: true,
        showCheckInAppointmentTab: this.hasACLicense,
        showBookScheduleTab: this.hasACLicense,
        restrictSignInCheckInOutsideHours: [{ value: false, disabled: false }]
      }, { validator: this.requireAtLeastOneCheckBoxToBeChecked(1, ['showSignInTab', 'showCheckInAppointmentTab', 'showBookScheduleTab']) }),

      // Option input fields
      inputFieldsGroup: this.formBuilder.group({
        showAccountNumber: false,
        showPhoneNumber: false,
        showEmailAddress: false,
        showDateOfBirth: false,
        showCustomField1: false,

        mandatoryAccountNumber: {value: false, disabled: true},
        mandatoryPhoneNumber: {value: false, disabled: true},
        mandatoryEmailAddress: {value: false, disabled: true},
        mandatoryDateOfBirth: {value: false, disabled: true},
        mandatoryCustomField1: {value: false, disabled: true}
      }),

      // Person Requested
      showPersonRequested: 'false',
      waitOptionShow:'false',
      showGroupRequested: { value: false, disabled: true },
      makePersonGroupRequestedMadatory: { value: false, disabled: true },
      defaultSelectedPersonOrGroup: { value: '-1', disabled: true },

      // Product service requested.
      showServiceRequested: 'true',
      showServiceRequestedCategorized: false,
      fontSize: 'medium',
      sortServiceListBy: this.productServiceOrder[0].value,
      showDisplayOrder: false,
      isProductServiceSelectionMandatory: false,

      // Wait time display options for home page and confirmation page
        showWaitTimeHome: false,
        showVisitorsWaitingHome: true,
        showWaitTimeConfirmation: false,
        showQueuePositionConfirmation: true,

      // Approximate waittime

      showWaitTime: 'false',
      waitOption: 'donotshow',
      waitVisitorsValue: [{ value: 0, disabled: true }, [Validators.required, Validators.min(0), Validators.max(999)]],
      waitHoursValue: [{ value: 0, disabled: true }, [Validators.required, Validators.min(0), Validators.max(999)]],

      // Additional settings
      refreshRateSeconds: [45, [Validators.required]],
      homeConfirmationPageRefreshSeconds: [10, [Validators.required]],
      defaultNotificationGroup: 0,
      showSpanishSpeaker: false,
      showChangeLanguageOption: false,

      // Welcome and Thank you text
      welcomeTextGroup: this.formBuilder.group({
        welcomeText: this.getLocalizedTextByLanguageAndType(1, 1).text,
        welcomeTextDropDown: '1'
      }, { validator: this.validateLocalizedWelcomeText }
      ),
      thankYouTextGroup: this.formBuilder.group({
        thankYouText: this.getLocalizedTextByLanguageAndType(1, 2).text,
        thankYouTextDropDown: '1'
      }, { validator: this.validateLocalizedThankyouText }
      )

    });
  }


  isACLicenseEnabled$: Observable<boolean> = new Observable<boolean>();
  isLTLicenseEnabled$: Observable<boolean> = new Observable<boolean>();
  public hasLTLicense: boolean = false;
  public waitTimeShow: boolean = false;
  public hasACLicense: boolean = false;
  public isValidWaitTime:boolean=true;
  private subscriptions = new Subscription();
  private sub: Subscription = new Subscription();
  public mode: string = '';
  public pageTitle: string = '';
  public userIdNameLookup: any[] = [];
  public notificationGroupList: NotificationGroup[] = [];
  public mergedUsersGroupsList: any[] = [];
  kioskConfigData: KioskConfiguration = new KioskConfiguration();
  kioskConfigurationForm: FormGroup;
  productServiceOrder = [{ text: 'Display Order', value: 'display_order' }, { text: 'Alphabetically', value: 'alphabetical' }];
  languageList = [{ text: 'English', id: '1' }, { text: 'Spanish', id: '2' }];
  locationName: string = '';
  welcomeTextList: LocalizedText[] = [];
  thankYouTextList: LocalizedText[] = [];
  public pageInfo: PageInfo = { PageSize: 10000, Page: 1 };
  public locationId: number = 0;
  public selectedPersonOrGroup: any = {userId:0,IsNotificationGroup:false };
  public userType!: UserType;
  public canShowGroup: boolean=false;
  public showWaitTimeErrorMessage:boolean=false;
  public sortInfo: SortInfo = { sortDirection: 1, SortColumn: 'firstName' };
  public canUncheckWaitTimeOption:boolean=true;
  // Show and Hide optional input field base on System config
  public isEnableAccountNumber: boolean = false;
  public isEnableCustomField: boolean = false;
  public accountNumberText: string = '';
  public customeFieldText: string = '';
  public kioskMode:string = '';
  public kioskTextList: any;


  ngOnInit() {
    this.translateService.get(['_General_English_EN_Text_','_General_Spanish_ES_Text_','_Kiosks_Add_Kiosk_Ts_Text_' , '_Kiosks_Edit_Kiosk_Ts_Text_' , '_Kiosks_Failed_Retrieving_Data_Ts_Text_' ,
      '_General_Large_Text_','_General_Medium_Text_', '_General_Small_Text_', '_Kiosk_Alphabetically_Text_', '_Kiosk_Display_Order_Text_', '_Kiosk_Configuration_One_Approx_Wait_Time_Opt_Select_Text_']).subscribe((data) => {
      this.languageList = [{ text: data['_General_English_EN_Text_'], id: '1' }, { text: data['_General_Spanish_ES_Text_'], id: '2' }];
      this.kioskTextList=[
      data['_Kiosks_Add_Kiosk_Ts_Text_'], 
      data['_Kiosks_Edit_Kiosk_Ts_Text_']];
      this.fontSizes =[{ text: data['_General_Small_Text_'], value: 'small' }, { text: data['_General_Medium_Text_'], value: 'medium' }, { text: data['_General_Large_Text_'], value: 'large' }];
      this.productServiceOrder = [{ text: data['_Kiosk_Display_Order_Text_'], value: 'display_order' }, { text: data['_Kiosk_Alphabetically_Text_'], value: 'alphabetical' }];
      this.approximateWaitTimeSelect = data['_Kiosk_Configuration_One_Approx_Wait_Time_Opt_Select_Text_'];
    });

    this.isLTLicenseEnabled$=this.store.select(getLTLicenseStatus);
    this.isACLicenseEnabled$=this.store.select(getACLicenseStatus);

    this.isEnableAccountNumber = false;
    this.isEnableCustomField = false;

    this.subscriptions.add(this.isLTLicenseEnabled$.subscribe((data: boolean) => {
      this.hasLTLicense = data;

    }));
    this.subscriptions.add(this.isACLicenseEnabled$.subscribe((data: boolean) => {
      this.hasACLicense = data;

    }));


    this.lookupService.getNotificationGroups().subscribe({
      next: (notificationGroups) => {
        this.notificationGroupList = notificationGroups;
      },
      error: (err) => { console.log(err) }
    })
    this.store.select(getUserType)
      .subscribe((userType: UserType | undefined) => {
        if (userType !== undefined && userType !== null) {
          this.userType = userType;
        }
      });

    this.pageInfo = { PageSize: 10000, Page: 1 };
    // Combine route parameters and query parameters to subscribe .
    let combined = combineLatest([this.route.params, this.route.queryParams]).pipe(map(([params, query]) => {
      return { params, query };
    }));

    this.sub = combined.subscribe(params => {
      const locationCode: string = params.params['locationCode'];
      const mode: string = params.query['mode'];
      const locationId: number = params.query['id'];
      this.locationName = params.query['name'];
      this.locationId = locationId;
      this.kioskMode=mode;
      this.getKioskConfigurations(locationCode, mode, locationId);
    });
    
    // Generate initial welcome and than you text
    this.welcomeTextList = this.generateLocalizedWelcomeTextList();
    this.thankYouTextList = this.generateLocalizedThankYouTextList();

    // Wait option change detection.


    // Welcome text dropdown value change detection
    this.kioskConfigurationForm.get("welcomeTextGroup.welcomeTextDropDown")!.valueChanges
      .subscribe(newValue => {
        this.kioskConfigurationForm.get("welcomeTextGroup.welcomeTextDropDown")!.markAsPristine();
        let welcomeTextDropdownDirty = this.kioskConfigurationForm.dirty;
        const oldValue = this.kioskConfigurationForm.value['welcomeTextGroup']['welcomeTextDropDown'];
        if ((newValue) && (oldValue)) {
          const welcome = this.getLocalizedTextByLanguageAndType(oldValue ?? 1, 1);
          const welComeTextControl = this.kioskConfigurationForm.get('welcomeTextGroup.welcomeText');
          welcome.text = welComeTextControl?.value;
          const newWelcomeText = this.getLocalizedTextByLanguageAndType(newValue ?? 1, 1).text;
          welComeTextControl?.setValue(newWelcomeText);
        }
        if(!welcomeTextDropdownDirty){
          this.kioskConfigurationForm.markAsPristine();
        }
      });

    // Thank you text dropdown value change detection
    this.kioskConfigurationForm.get('thankYouTextGroup.thankYouTextDropDown')!.valueChanges
      .subscribe(newValue => {
        this.kioskConfigurationForm.get('thankYouTextGroup.thankYouTextDropDown')!.markAsPristine();
        let thankYouTextDropdownDirty = this.kioskConfigurationForm.dirty;
        const oldValue = this.kioskConfigurationForm.value['thankYouTextGroup']['thankYouTextDropDown'];
        if ((newValue) && (oldValue)) {
          const thankYouText = this.getLocalizedTextByLanguageAndType(oldValue ?? 1, 2);
          const thankYouTextControl = this.kioskConfigurationForm.get('thankYouTextGroup.thankYouText');
          thankYouText.text = thankYouTextControl?.value;
          const newthankYouText = this.getLocalizedTextByLanguageAndType(newValue ?? 1, 2).text;
          thankYouTextControl?.setValue(newthankYouText);
        }
        if(!thankYouTextDropdownDirty){
          this.kioskConfigurationForm.markAsPristine();
        }
      });

      this.kioskConfigurationForm.get('showWaitTime')!.valueChanges
      .subscribe(value => {
        this.showHideWaitTime(value === 'true');
      });

    // Get users for person group requested.
    // Lookup data.
    this.validateSystemPermission();
    if(this.kioskMode=='add'){
      this.setWaitOptionValue(false,'',0); // Initially no wait time options selected
    }  
    
    const tabsGroup = this.kioskConfigurationForm.get('tabsGroup');

    tabsGroup!.get('showSignInTab')!.valueChanges.subscribe(() => {
      this.updateRestrictOutsideHoursState();
    });

    tabsGroup!.get('showCheckInAppointmentTab')!.valueChanges.subscribe(() => {
      this.updateRestrictOutsideHoursState();
    });
      }
  validateShowWaitTime(){
    this.isValidWaitTime=true;
  }

  hasUnsavedData(): boolean {
    return this.kioskConfigurationForm.dirty;
  }
  get filterByGroup() {
    if (this.canShowGroup) {
      return this.mergedUsersGroupsList;
    } else {
      return this.userIdNameLookup;
    }
  }

  private updateRestrictOutsideHoursState(): void {
    const tabs = this.kioskConfigurationForm.get('tabsGroup')!;

    const signIn = tabs.get('showSignInTab')!.value;
    const appt = tabs.get('showCheckInAppointmentTab')!.value;

    const restrictControl = tabs.get('restrictSignInCheckInOutsideHours')!;

    if (!signIn && !appt) {
      restrictControl.disable();
    } else {
      restrictControl.enable();
    }
  }
  // Get all users for default user and group.
  private getAllUsers() {
    let userGridRequestModel = this.getUserParams();
    this.userService.getUsersForTheGrid(userGridRequestModel).subscribe({
      next: (response: any) => {
        this.userIdNameLookup = response.data.slice();
        this.mergedUsersGroupsList = this.mergeGroupAndUsers(this.userIdNameLookup, this.notificationGroupList);

        //If already have some value
        const userNotificationList = this.selectedPersonOrGroup.IsNotificationGroup
          ? this.mergedUsersGroupsList
          : this.userIdNameLookup;
        if(this.selectedPersonOrGroup.userId>0){
          var selectedPersonOrGroupObj = userNotificationList.find(x=>x.userId==this.selectedPersonOrGroup.userId && x.IsNotificationGroup==this.selectedPersonOrGroup.IsNotificationGroup);
          if(selectedPersonOrGroupObj){
            this.kioskConfigurationForm.patchValue({defaultSelectedPersonOrGroup:selectedPersonOrGroupObj});
          }
          else{
            this.kioskConfigurationForm.patchValue({defaultSelectedPersonOrGroup:"-1"});
          }

        }
        else{
          this.kioskConfigurationForm.patchValue({defaultSelectedPersonOrGroup:"-1"});
        }
      },
      error: (error) => {console.log(error);}
    });


  }
  //merge notification groups with users
  private mergeGroupAndUsers(users: any, groups: NotificationGroup[]): any {
    let mergedList = new Array();
    mergedList = users.slice();

    mergedList.forEach(function (user) {
      user.IsNotificationGroup = false;
    });
    groups.forEach((group) => {
      mergedList.push({ firstName: group.name, IsNotificationGroup: true, userId: group.id })
    });
    return mergedList;
  }

  private getUserParams(): UserGridRequestModel {
    let userGridRequestModel = new UserGridRequestModel();
    userGridRequestModel.IncludeSubLocations = false;
    userGridRequestModel.PageInfo = this.pageInfo;
    userGridRequestModel.SearchText = "";
    userGridRequestModel.Status = 1;
    userGridRequestModel.SortInfo = this.sortInfo;
    userGridRequestModel.LocationId = this.locationId;
    userGridRequestModel.UserType = this.userType;
    userGridRequestModel.RequestFrom="KIOSKCONFIG";
    userGridRequestModel.PermissionGroupId = 0;

    return userGridRequestModel;
  }


  setWelcomeTextWhenTextChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const welcomeText = target.value;
    const welcomeDropDownValue = this.kioskConfigurationForm.get('welcomeTextGroup.welcomeTextDropDown')!.value;
    const welcomeLocalizedText = this.getLocalizedTextByLanguageAndType(welcomeDropDownValue, 1);
    welcomeLocalizedText.text = welcomeText;
    this.kioskConfigurationForm.markAsDirty();
  }

  setthankYouTextWhenTextChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const thankYouText = target.value;
    const thankYouDropDownValue = this.kioskConfigurationForm.get('thankYouTextGroup.thankYouTextDropDown')!.value;
    const thankYouLocalizedText = this.getLocalizedTextByLanguageAndType(thankYouDropDownValue, 2);
    thankYouLocalizedText.text = thankYouText;
    this.kioskConfigurationForm.markAsDirty();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getKioskConfigurations(locationCode: string, mode: string, locationId: number) {
if (mode === 'add' || !(locationCode)) {
  this.pageTitle = `${this.kioskTextList[0]} ${this.locationName}`;
  this.mode = mode;
  this.kioskConfigData = this.initializeKioskConfiguration();
  this.kioskConfigData.locationId = locationId;

  this.welcomeTextList = this.generateLocalizedWelcomeTextList();
  this.thankYouTextList = this.generateLocalizedThankYouTextList();

  this.kioskConfigurationForm.get('welcomeTextGroup.welcomeTextDropDown')?.setValue('1');
  this.kioskConfigurationForm.get('welcomeTextGroup.welcomeText')?.setValue(this.getLocalizedTextByLanguageAndType(1, 1).text);

  this.kioskConfigurationForm.get('thankYouTextGroup.thankYouTextDropDown')?.setValue('1');
  this.kioskConfigurationForm.get('thankYouTextGroup.thankYouText')?.setValue(this.getLocalizedTextByLanguageAndType(1, 2).text);

  this.kioskConfigurationForm.updateValueAndValidity();

  this.getAllUsers();

  } else {
      this.pageTitle = `${this.kioskTextList[1]} - ${this.locationName}`;
      this.mode = mode;
      this.kioskConfigService.getKioskConfiguration(locationCode).subscribe({
        next: (kioskConfiguration: KioskConfiguration) => this.displayKioskConfigurations(kioskConfiguration),
        error: (err) => {
          this.handleError(err, '_Kiosks_Failed_Retrieving_Data_Ts_Text_');
        }
      });
    }
  }

  handleError(err: any, errorMessage: string) {
    console.log(err);
    this.showErrorToast(errorMessage);
  }
  // Validations
  validateWaitOptionValue(waitOptionValue: string) {
    const waitHoursValueControl = this.kioskConfigurationForm.get('waitHoursValue');
    const waitVisitorsControl = this.kioskConfigurationForm.get('waitVisitorsValue');
    const showWaitTime = this.kioskConfigurationForm.get('showWaitTime')!.value;

    if (waitOptionValue === 'fullday') {
      waitHoursValueControl?.disable();
      waitVisitorsControl?.disable();
      waitHoursValueControl?.setValue(0);
      waitVisitorsControl?.setValue(0);
    } else if (waitOptionValue === 'visitors') {
      waitVisitorsControl?.enable();
      waitHoursValueControl?.setValue(0);
      waitHoursValueControl?.disable();
    } else if(waitOptionValue=='hours') {

      waitVisitorsControl?.setValue(0);
      waitVisitorsControl?.disable();
      waitHoursValueControl?.enable();
    }
    else{
      waitVisitorsControl?.disable();
      waitHoursValueControl?.disable();
    }
  }

  setWaitOptionValue(showWaitTime:boolean, waitOption: string, waitValue: number) {
    const waitHoursValueControl = this.kioskConfigurationForm.get('waitHoursValue');
    const waitVisitorsControl = this.kioskConfigurationForm.get('waitVisitorsValue');

    if (waitOption === 'visitors') {
      waitVisitorsControl?.setValue(waitValue);
      waitVisitorsControl?.enable();
      waitHoursValueControl?.setValue(0);
      waitHoursValueControl?.disable();
    } else if (waitOption === 'hours') {
      waitHoursValueControl?.setValue(waitValue);
      waitHoursValueControl?.enable();
      waitVisitorsControl?.setValue(0);
      waitVisitorsControl?.disable();
    } else {
      waitHoursValueControl?.disable();
      waitVisitorsControl?.disable();
      waitHoursValueControl?.setValue(0);
      waitVisitorsControl?.setValue(0);
    }
    this.showHideWaitTime(showWaitTime);

  }


  private enableDisableMandatoryCheckBoxes() {
    const inputFormGroup = this.kioskConfigurationForm.get('inputFieldsGroup')!;
    if (inputFormGroup.get('showAccountNumber')?.value) {
      const control = inputFormGroup.get('mandatoryAccountNumber')!;
      control.enable();
    } else {
      const control = inputFormGroup.get('mandatoryAccountNumber');
      control?.setValue(false);
      control?.disable();
    }
    if (inputFormGroup.get('showPhoneNumber')?.value) {
      const control = inputFormGroup.get('mandatoryPhoneNumber')!;
      control.enable();
    } else {
      const control = inputFormGroup.get('mandatoryPhoneNumber');
      control?.setValue(false);
      control?.disable();
    }
    if (inputFormGroup.get('showEmailAddress')?.value) {
      const control = inputFormGroup.get('mandatoryEmailAddress')!;
      control.enable();
    } else {
      const control = inputFormGroup.get('mandatoryEmailAddress');
      control?.setValue(false);
      control?.disable();
    }
    if (inputFormGroup.get('showDateOfBirth')?.value) {
      const control = inputFormGroup.get('mandatoryDateOfBirth')!;
      control.enable();
    } else {
      const control = inputFormGroup?.get('mandatoryDateOfBirth');
      control?.setValue(false);
      control?.disable();
    }
    if (inputFormGroup.get('showCustomField1')?.value) {
      const control = inputFormGroup.get('mandatoryCustomField1')!;
      control.enable();
    } else {
      const control = inputFormGroup.get('mandatoryCustomField1');
      control?.setValue(false);
      control?.disable();
    }
  }

  public onlyFourInputOptionsAllowed() {
    let checked = 0;
    const inputFormGroup = this.kioskConfigurationForm.get('inputFieldsGroup');
    const notCheckedArray: any[] = [];
    const inputFromArray = <FormArray>inputFormGroup;
    Object.keys(inputFromArray.controls).forEach(key => {
      // Verify wheather only 4 show option selected and remove mandatory options
      if (key.startsWith("show")) {
        const control = inputFormGroup?.get(key);
        if (control?.value === true) {
          checked++;
        } else {
          notCheckedArray.push(control);
        }
      }
    });
    if (checked === 4) {
      notCheckedArray.forEach(c => c.disable());
    } else {
      notCheckedArray.forEach(c => c.enable());
    }
    this.enableDisableMandatoryCheckBoxes();


  }

  requireAtLeastOneCheckBoxToBeChecked(minRequired = 1, fieldKeys?: string[]): ValidatorFn {
    return function validate(formGroupControl: AbstractControl): { [key: string]: boolean } | null {
      const targetKeys = fieldKeys ?? Object.keys((formGroupControl as FormArray).controls);
      const checked = targetKeys.reduce((count, key) => {
        const control = formGroupControl.get(key);
        return control?.value === true ? count + 1 : count;
      }, 0);

      if (checked < minRequired) {
        return { requireOneCheckboxToBeChecked: true };
      }

      return null;
    };
  }

  validateLocalizedWelcomeText(formGroup: FormGroup): { [key: string]: boolean } | null {
    const welcomeText = formGroup.get('welcomeText');
    const welcomeTextDropDown = formGroup.get('welcomeTextDropDown');
    if (welcomeTextDropDown?.value === '1' && !(welcomeText?.value)) {
      return { 'welcomeTextRequired': true };
    }
    return null;
  }

  validateLocalizedThankyouText(formGroup: FormGroup): { [key: string]: boolean } | null {
    const thankYouText = formGroup.get('thankYouText');
    const thankYouTextDropDown = formGroup.get('thankYouTextDropDown');
    if (thankYouTextDropDown?.value === '1' && !(thankYouText?.value)) {
      return { 'thankYouTextRequired': true };
    }
    return null;

  }

  // End of validations.


  setPersonRequested(showPersonRequested: boolean) {
    const showGroupRequstedControl = this.kioskConfigurationForm.get('showGroupRequested');
    const defaultPersonGroupControl = this.kioskConfigurationForm.get('defaultSelectedPersonOrGroup');
    const makePersonGroupRequestedMadatoryControl = this.kioskConfigurationForm.get('makePersonGroupRequestedMadatory');
    if (showPersonRequested) {
      showGroupRequstedControl?.enable();
      defaultPersonGroupControl?.enable();
      makePersonGroupRequestedMadatoryControl?.enable();
    } else {
      showGroupRequstedControl?.disable();
      defaultPersonGroupControl?.disable();
      makePersonGroupRequestedMadatoryControl?.setValue(false);
      makePersonGroupRequestedMadatoryControl?.disable();
    }
  }
  setWaitTimeCalculation(){

    const waitTimeHome = this.kioskConfigurationForm.get('showWaitTimeHome')?.value;
    const visitorsWaitingHome = this.kioskConfigurationForm.get('showVisitorsWaitingHome')?.value;
    const waitTimeConfirmation = this.kioskConfigurationForm.get('showWaitTimeConfirmation')?.value;
    const queuePositionConfirmation = this.kioskConfigurationForm.get('showQueuePositionConfirmation')?.value;
    const waitOption = this.kioskConfigurationForm.get('waitOption');
    const waitHoursValueControl = this.kioskConfigurationForm.get('waitHoursValue');
    const waitVisitorsControl = this.kioskConfigurationForm.get('waitVisitorsValue');
    let waitOptionValue = this.kioskConfigurationForm.get('waitOption')?.value;

    const checkedCount = [waitTimeHome, visitorsWaitingHome, waitTimeConfirmation, queuePositionConfirmation].filter(v => v).length;

    const showWaitTimeControl = this.kioskConfigurationForm.get('showWaitTime');
    if (checkedCount > 0) {
      showWaitTimeControl?.setValue('true');
    } else {
      showWaitTimeControl?.setValue('false');
    }

    if(waitTimeHome || waitTimeConfirmation){
      if (waitOptionValue == WaitTimeOption.DoNotShow) {
        waitOptionValue = WaitTimeOption.ByNumHours;
        waitOption!.setValue(waitOptionValue);
      }
      this.validateWaitOptionValue(waitOptionValue);
      waitOption?.enable();
      this.showWaitTimeErrorMessage=true;
    }
    else{
      waitHoursValueControl?.disable();
      waitVisitorsControl?.disable();
      waitOption?.disable();
      this.showWaitTimeErrorMessage=false;
    }
    this.validateShowWaitTime();
  }
  showHideWaitTime(showWaitTime: boolean){
    const waitHoursValueControl = this.kioskConfigurationForm.get('waitHoursValue');
    const waitVisitorsControl = this.kioskConfigurationForm.get('waitVisitorsValue');
    const waitOption = this.kioskConfigurationForm.get('waitOption');
    if(showWaitTime==false){
      waitHoursValueControl?.disable();
      waitVisitorsControl?.disable();
      waitOption?.disable();
    }
    else{
      this.setWaitTimeCalculation();
      this.validateWaitOptionValue(this.kioskConfigurationForm.get('waitOption')?.value);
    }
  }

  public showServiceRequest(serviceRequestedOption: boolean) {
    const sortserviceByControl = this.kioskConfigurationForm.get('sortServiceListBy');
    const fontSizeControl = this.kioskConfigurationForm.get('fontSize');
    const showServiceRequestedCategorizedControl = this.kioskConfigurationForm.get('showServiceRequestedCategorized');
    const isProductServiceSelectionMandatory = this.kioskConfigurationForm.get('isProductServiceSelectionMandatory');
    if (serviceRequestedOption) {
      sortserviceByControl?.enable();
      fontSizeControl?.enable();
      showServiceRequestedCategorizedControl?.enable();
      isProductServiceSelectionMandatory?.enable();
    } else {
      sortserviceByControl?.disable();
      fontSizeControl?.disable();
      showServiceRequestedCategorizedControl?.disable();
      isProductServiceSelectionMandatory?.disable();
    }
  }

  displayKioskConfigurations(kioskConfigurationData: KioskConfiguration) {
    console.log('Received kiosk configuration data:', kioskConfigurationData);
    if (this.kioskConfigurationForm) {
      this.kioskConfigurationForm.reset();
    }
    this.kioskConfigData = kioskConfigurationData;
    var selectedPersonOrGroup={};
    //Notification Group Selected
    if(kioskConfigurationData.defaultSelectedPersonOrGroup && kioskConfigurationData.defaultSelectedPersonOrGroup.startsWith("G")){
      var groupId=Number(kioskConfigurationData.defaultSelectedPersonOrGroup.replace("G", ""));
      this.selectedPersonOrGroup.userId=groupId;
      this.selectedPersonOrGroup.IsNotificationGroup=true;

    }
    else if(kioskConfigurationData.defaultSelectedPersonOrGroup!="-1"){
      this.selectedPersonOrGroup.userId=kioskConfigurationData.defaultSelectedPersonOrGroup;
      this.selectedPersonOrGroup.IsNotificationGroup=false;

    }

    this.kioskConfigurationForm.patchValue({
      tabsGroup: {
        showBookScheduleTab: kioskConfigurationData.showScheduleTab,
        showCheckInAppointmentTab: kioskConfigurationData.showCheckInAppointmentTab,
        showSignInTab: kioskConfigurationData.showSignInTab,
        restrictSignInCheckInOutsideHours: kioskConfigurationData.restrictSignInCheckInOutsideHours
      },
      inputFieldsGroup: {
        showAccountNumber: kioskConfigurationData.showAccountNumber,
        showPhoneNumber: kioskConfigurationData.showPhoneNumber,
        showEmailAddress: kioskConfigurationData.showEmailAddress,
        showDateOfBirth: kioskConfigurationData.showDateOfBirth,
        showCustomField1: kioskConfigurationData.showCustomField1,
        mandatoryAccountNumber: kioskConfigurationData.mandatoryAccountNumber,
        mandatoryPhoneNumber: kioskConfigurationData.mandatoryPhoneNumber,
        mandatoryEmailAddress: kioskConfigurationData.mandatoryEmailAddress,
        mandatoryDateOfBirth: kioskConfigurationData.mandatoryDateOfBirth,
        mandatoryCustomField1: kioskConfigurationData.mandatoryCustomField1
      },
      showPersonRequested: kioskConfigurationData.showPersonRequested.toString(),
      waitOptionShow:false,
      showGroupRequested: kioskConfigurationData.showGroupRequested,
      makePersonGroupRequestedMadatory: kioskConfigurationData.makePersonGroupRequestedMadatory,
      defaultSelectedPersonOrGroup: selectedPersonOrGroup ?? "",
      showServiceRequested: kioskConfigurationData.showServiceRequested.toString(),
      showServiceRequestedCategorized: kioskConfigurationData.showServiceRequestedCategorized,
      fontSize: kioskConfigurationData.fontSize,
      sortServiceListBy: kioskConfigurationData.sortServiceListBy,
      showDisplayOrder: kioskConfigurationData.showDisplayOrder,
      isProductServiceSelectionMandatory: kioskConfigurationData.isProductServiceSelectionMandatory,
      showWaitTimeHome: kioskConfigurationData.showWaitTimeHome,
      showVisitorsWaitingHome: kioskConfigurationData.showVisitorsWaitingHome,
      showWaitTimeConfirmation: kioskConfigurationData.showWaitTimeConfirmation,
      showQueuePositionConfirmation: kioskConfigurationData.showQueuePositionConfirmation,
      waitOption: kioskConfigurationData.waitOption,
      refreshRateSeconds: kioskConfigurationData.refreshRateSeconds,
      homeConfirmationPageRefreshSeconds: kioskConfigurationData.lastPageRefreshRateSeconds,
      showSpanishSpeaker: kioskConfigurationData.showSpanishSpeaker,
      showChangeLanguageOption: kioskConfigurationData.showChangeLanguageOption,
      defaultNotificationGroup:kioskConfigurationData.defaultNotificationGroup==null? 0:kioskConfigurationData.defaultNotificationGroup
    });
    this.onlyFourInputOptionsAllowed();
    this.setWaitTimeCalculation(); // This will set showWaitTime based on the four checkboxes
    this.setWaitOptionValue(kioskConfigurationData.showWaitTime,kioskConfigurationData.waitOption, kioskConfigurationData.waitValue);
    this.showServiceRequest(kioskConfigurationData.showServiceRequested);
    this.setPersonRequested(kioskConfigurationData.showPersonRequested);
    this.getInitialLocalizedWelcomeText(kioskConfigurationData.localizedWelcomeTextList);
    this.getLocalizedThankYouText(kioskConfigurationData.localizedThankyouTextList);
    this.kioskConfigurationForm.updateValueAndValidity();
    this.getAllUsers();

  }

  backKioskConfigurationGrid() {
    this.router.navigate(['admin/kiosk/kiosk-grid']);
  }

  private initializeKioskConfiguration(): KioskConfiguration {

    return {
      showScheduleTab: this.hasACLicense,
      showCheckInAppointmentTab: this.hasACLicense,
      showSignInTab: true,
      restrictSignInCheckInOutsideHours: false,
      showAccountNumber: false,
      showPhoneNumber: false,
      showEmailAddress: false,
      showDateOfBirth: false,
      showCustomField1: false,
      mandatoryAccountNumber: false,
      mandatoryPhoneNumber: false,
      mandatoryEmailAddress: false,
      mandatoryDateOfBirth: false,
      mandatoryCustomField1: false,
      showPersonRequested: false,
      showWaitTime:false,
      showGroupRequested: false,
      makePersonGroupRequestedMadatory: false,
      defaultSelectedPersonOrGroup: '-1',
      showServiceRequested: false,
      showServiceRequestedCategorized: false,
      showWaitTimeHome: false,
      showVisitorsWaitingHome: true,
      showWaitTimeConfirmation: false,
      showQueuePositionConfirmation: true,
      isProductServiceSelectionMandatory: false,
      defaultNotificationGroup: 0,
      sortServiceListBy: 'display_order',
      waitOption: 'donotshow',
      waitValue: 0,
      locationName: this.locationName,
      locationId: 0,
      showSpanishSpeaker: false,
      showChangeLanguageOption: false,
      refreshRateSeconds: 15,
      lastPageRefreshRateSeconds: 25,
      fontSize: 'medium',
      localizedWelcomeTextList: [],
      localizedThankyouTextList: [],
      defaultWelcomeText: '',
      defaultThankYouText: '',
      showDisplayOrder: false

    };

  }

  // Generate initial welcome text.
  private generateLocalizedWelcomeTextList(): LocalizedText [] {
    const englishText = new LocalizedText();
    englishText.languageId = 1;
    englishText.localizedFieldId = 1;
    englishText.localizedTextId = 0;
    englishText.text = `Welcome to ${this.locationName}!`;

    const spanishText = new LocalizedText();
    spanishText.languageId = 2;
    spanishText.localizedFieldId = 1;
    spanishText.localizedTextId = 0;
    spanishText.text = '';

    return [englishText, spanishText];

  }

  // Get initial thank you text.
  private generateLocalizedThankYouTextList(): LocalizedText[] {
    const englishText = new LocalizedText();
    englishText.languageId = 1;
    englishText.localizedFieldId = 2;
    englishText.localizedTextId = 0;
    englishText.text = `Thank you!`;

    const spanishText = new LocalizedText();
    spanishText.languageId = 2;
    spanishText.localizedFieldId = 2;
    spanishText.localizedTextId = 0;
    spanishText.text = '';
    return [englishText, spanishText];
  }

  getLocalizedTextByLanguageAndType(languageId: number, localizedFieldId: number): LocalizedText {
    if (localizedFieldId === 1) {
      return this.welcomeTextList.find(w => w.languageId == languageId) ?? new LocalizedText();
    } else {
      return this.thankYouTextList.find(t => t.languageId == languageId) ?? new LocalizedText();
    }
  }



  saveConfiguration() {
    if (this.kioskConfigurationForm.valid) {
      const configurationData = this.getKioskConfigDataForSaving();
      

      if (this.mode === 'add') {
        this.kioskConfigService.addKioskConfiguration(configurationData).subscribe({
          next: () => this.onAddKioskConfigurationComplete('_Kiosks_Configured_Successfully_Ts_Text_'),
          error: (err) => this.onsaveConfigurationError(err, '_Kiosks_Configuration_Failed_Ts_Text_')
        });
      } else if (this.kioskConfigurationForm.dirty && configurationData.locationId > 0) {
        this.kioskConfigService.updateKioskConfiguration(configurationData)
          .subscribe({
            next: () => this.onSaveConfigurationComplete('_General_Changes_Saved_Successfully_Ts_Text_'),
            error: (err) => this.onsaveConfigurationError(err, '_General_Saving_Failed_Ts_Text_')
          });
      }
    }
  }

  private getKioskConfigDataForSaving(): KioskConfiguration {
    const kioskFormValues = { ...this.kioskConfigurationForm.getRawValue() };
    const kioskData = new KioskConfiguration();

    kioskData.showScheduleTab = kioskFormValues.tabsGroup.showBookScheduleTab;
    kioskData.showCheckInAppointmentTab = kioskFormValues.tabsGroup.showCheckInAppointmentTab;
    kioskData.showSignInTab = kioskFormValues.tabsGroup.showSignInTab;
    kioskData.restrictSignInCheckInOutsideHours = kioskFormValues.tabsGroup.restrictSignInCheckInOutsideHours
    kioskData.showAccountNumber = kioskFormValues.inputFieldsGroup.showAccountNumber ?? false;
    kioskData.showPhoneNumber = kioskFormValues.inputFieldsGroup.showPhoneNumber;
    kioskData.showEmailAddress = kioskFormValues.inputFieldsGroup.showEmailAddress;
    kioskData.showDateOfBirth = kioskFormValues.inputFieldsGroup.showDateOfBirth;
    kioskData.showCustomField1 = kioskFormValues.inputFieldsGroup.showCustomField1;
    kioskData.mandatoryAccountNumber = kioskFormValues.inputFieldsGroup.mandatoryAccountNumber;
    kioskData.mandatoryPhoneNumber = kioskFormValues.inputFieldsGroup.mandatoryPhoneNumber;
    kioskData.mandatoryEmailAddress = kioskFormValues.inputFieldsGroup.mandatoryEmailAddress;
    kioskData.mandatoryDateOfBirth = kioskFormValues.inputFieldsGroup.mandatoryDateOfBirth;
    kioskData.mandatoryCustomField1 = kioskFormValues.inputFieldsGroup.mandatoryCustomField1;
    kioskData.showPersonRequested = kioskFormValues.showPersonRequested === 'true';
    kioskData.showGroupRequested = kioskFormValues.showGroupRequested;
    kioskData.makePersonGroupRequestedMadatory = kioskFormValues.makePersonGroupRequestedMadatory;
    if(kioskFormValues.defaultSelectedPersonOrGroup!="-1"){
      kioskData.defaultSelectedPersonOrGroup = kioskFormValues.defaultSelectedPersonOrGroup.IsNotificationGroup? this.canShowGroup? "G"+kioskFormValues.defaultSelectedPersonOrGroup.userId:"-1":kioskFormValues.defaultSelectedPersonOrGroup.userId;
    }
    else{
      kioskData.defaultSelectedPersonOrGroup=kioskFormValues.defaultSelectedPersonOrGroup;
    }
    kioskData.showServiceRequested = kioskFormValues.showServiceRequested === 'true';
    kioskData.showServiceRequestedCategorized = kioskFormValues.showServiceRequestedCategorized;
    kioskData.isProductServiceSelectionMandatory = kioskFormValues.isProductServiceSelectionMandatory;
    kioskData.defaultNotificationGroup = kioskFormValues.defaultNotificationGroup;
    kioskData.sortServiceListBy = kioskFormValues.sortServiceListBy ?? 'display_order';
    kioskData.locationId = this.kioskConfigData.locationId;
    kioskData.locationName = this.locationName;
    kioskData.showSpanishSpeaker = kioskFormValues.showSpanishSpeaker;
    kioskData.showChangeLanguageOption = kioskFormValues.showChangeLanguageOption;
    kioskData.refreshRateSeconds = kioskFormValues.refreshRateSeconds;
    kioskData.lastPageRefreshRateSeconds = kioskFormValues.homeConfirmationPageRefreshSeconds;
    kioskData.showWaitTimeHome = kioskFormValues.showWaitTimeHome;
    kioskData.showVisitorsWaitingHome = kioskFormValues.showVisitorsWaitingHome;
    kioskData.showWaitTimeConfirmation = kioskFormValues.showWaitTimeConfirmation;
    kioskData.showQueuePositionConfirmation = kioskFormValues.showQueuePositionConfirmation;
    kioskData.fontSize = kioskFormValues.fontSize ?? 'medium';
    kioskData.showDisplayOrder = kioskFormValues.showDisplayOrder;
    kioskData.waitOption = kioskFormValues.waitOption;
    kioskData.showWaitTime = kioskFormValues.showWaitTime === 'true';
    kioskData.waitValue=0;
    if(kioskData.waitOption=="hours"){
      kioskData.waitValue =kioskFormValues.waitHoursValue ;
    }
    else if(kioskData.waitOption=="visitors"){
      kioskData.waitValue =kioskFormValues.waitVisitorsValue ;
    }

    kioskData.defaultWelcomeText = this.getLocalizedTextByLanguageAndType(1, 1).text;
    kioskData.defaultThankYouText = this.getLocalizedTextByLanguageAndType(1, 2).text;
    kioskData.localizedWelcomeTextList = this.welcomeTextList;
    kioskData.localizedThankyouTextList = this.thankYouTextList;
    this.setWaitOptions(kioskData, kioskFormValues);
    return kioskData;
  }

  private setWaitOptions(configuration: KioskConfiguration, kioskFormData: any) {
    if(configuration.showWaitTime==true){
      if (configuration.waitOption === 'visitors') {
        configuration.waitValue = kioskFormData.waitVisitorsValue;
      } else if (configuration.waitOption === 'hours') {
        configuration.waitValue = kioskFormData.waitHoursValue;
      } else if (configuration.waitOption === 'fullday') {
        configuration.waitValue = 0;
      } else {
        configuration.waitValue = 0;

      }
    }

  }

  getInitialLocalizedWelcomeText(localizedWelcomeTextList: any[]) {
    this.welcomeTextList = localizedWelcomeTextList;
    const welcomeTextDropDown = this.kioskConfigurationForm.get('welcomeTextGroup.welcomeTextDropDown');
    welcomeTextDropDown?.setValue('1');
    const welcomeText = this.kioskConfigurationForm.get('welcomeTextGroup.welcomeText');
    const welcomeTextValue = this.getLocalizedTextByLanguageAndType(1, 1);
    welcomeText?.setValue(welcomeTextValue.text);
  }

  getLocalizedThankYouText(localizedThankYouTextList: any[]) {
    this.thankYouTextList = localizedThankYouTextList;
    const thankYouTextDropDown = this.kioskConfigurationForm.get('thankYouTextGroup.thankYouTextDropDown');
    thankYouTextDropDown?.setValue('1');
    const thankYouTextValue = this.getLocalizedTextByLanguageAndType(1, 2);
    const thankYouText = this.kioskConfigurationForm.get('thankYouTextGroup.thankYouText');
    thankYouText?.setValue(thankYouTextValue.text);
  }

  private onAddKioskConfigurationComplete(msg: string) {
    this.onSaveConfigurationComplete(msg);
    this.backToKioskGrid();
  }

  private onSaveConfigurationComplete(msg: string) {
    this.kioskConfigurationForm.markAsPristine();
    this.showSuccessToast(msg);
  }

  private onsaveConfigurationError(error: any, msg: string) {
    this.showErrorToast(msg);
  }

  backToKioskGrid() {
    this.router.navigate(['admin/kiosk-grid']);
  }

  showErrorToast(errorMessage: string) {
    this.toastService.showError(errorMessage);

  }

  showSuccessToast(successMessage: string) {
    this.toastService.showSuccess(successMessage);
  }

  private validateSystemPermission() {
    this.systemConfigService.getSystemConfigurationForRoot().subscribe(
      configs => {
        let lobbySystemSettings: SystemConfigValue[] = configs.filter(c => c.configurationGroup === 'LobbySettings');
        let accountSetting = lobbySystemSettings.filter(sc => sc.configurationId === SystemConfigurationValueType.EnableAccountNumber);
        const customTextFieldValue = readCastedValue(lobbySystemSettings.find(x=>x.configurationName=='CustomFieldText'));
        if (typeof customTextFieldValue === 'string') {
          this.customeFieldText = customTextFieldValue;
        }
        
        const accountNumberTextValue = readCastedValue(lobbySystemSettings.find(x=>x.configurationName=='AccountNumberDisplayText'));
        if (typeof accountNumberTextValue === 'string') {
          this.accountNumberText = accountNumberTextValue;
        }

        if (accountSetting.length === 1 && accountSetting[0].value === "1"){
          this.isEnableAccountNumber = true;
        } else {
          this.isEnableAccountNumber = false;
        }

        let customFeildSettings = lobbySystemSettings.filter(sc => sc.configurationId === SystemConfigurationValueType.EnableCustomField);
        if (customFeildSettings.length === 1 && customFeildSettings[0].value === "1"){
          this.isEnableCustomField = true;
        } else {
          this.isEnableCustomField = false;
        }
      }
    )
  }
}
